import type { FileAccessLevel, FilePolicyRule, ToolPolicyRule } from "@/lib/api";
import { filePolicyRulePatternError, hasControlCharacter } from "@/lib/file-policy-rules";
import {
	ARG_PATTERN_TOOL_NAMES,
	POLICY_TOOL_NAMES,
	policyToolName,
	runtimeToolName,
} from "@/lib/tool-catalog";

type PolicyAction = ToolPolicyRule["action"];

/** deny → ask → allow の順で出力・検証する。 */
const ACTIONS: PolicyAction[] = ["deny", "ask", "allow"];

/** ファイルルールを擬似エントリへ写す対応表。Read は deny だけに現れる。 */
const FILE_ENTRY_BY_ACCESS: Record<FileAccessLevel, { action: PolicyAction; verb: string }> = {
	denied: { action: "deny", verb: "Read" },
	readOnly: { action: "deny", verb: "Edit" },
	confirmEdit: { action: "ask", verb: "Edit" },
	editable: { action: "allow", verb: "Edit" },
};

const ACCESS_BY_EDIT_ACTION: Record<PolicyAction, FileAccessLevel> = {
	deny: "readOnly",
	ask: "confirmEdit",
	allow: "editable",
};

/** サーバ側の上限と揃える。 */
const MAX_RULES = 500;
const MAX_ARG_PATTERN_LENGTH = 1000;

export type ToolPolicyDocumentParseResult =
	| { tools: ToolPolicyRule[]; files: FilePolicyRule[] }
	| { error: string };

/**
 * 保存済みルールを編集用 JSON へ変換する。deny/ask/allow は空でも出力し、
 * 各配列はツールルール→ファイル擬似エントリの順に昇順で並べて決定的にする。
 */
export function serializeToolPolicyDocument(
	toolRules: readonly ToolPolicyRule[],
	fileRules: readonly FilePolicyRule[],
): string {
	const toolEntries: Record<PolicyAction, string[]> = { deny: [], ask: [], allow: [] };
	const fileEntries: Record<PolicyAction, string[]> = { deny: [], ask: [], allow: [] };
	for (const rule of toolRules) {
		toolEntries[rule.action].push(formatToolPolicyEntry(rule));
	}
	for (const rule of fileRules) {
		fileEntries[FILE_ENTRY_BY_ACCESS[rule.access].action].push(formatFilePolicyEntry(rule));
	}
	const permissions: Record<PolicyAction, string[]> = { deny: [], ask: [], allow: [] };
	for (const action of ACTIONS) {
		permissions[action] = [...toolEntries[action].sort(), ...fileEntries[action].sort()];
	}
	return JSON.stringify({ permissions }, null, 2);
}

/** JSON を保存用の入力へ変換する。エラーは最初の 1 件だけ返す。 */
export function parseToolPolicyDocument(text: string): ToolPolicyDocumentParseResult {
	const source = text.replace(/^\uFEFF/, "");
	if (source.trim() === "") return { error: "JSON を入力してください。" };

	let root: unknown;
	try {
		root = JSON.parse(source);
	} catch {
		// 構文エラーは利用者が直せる入力ミスなので、例外ではなく検証結果として返す。
		return { error: "JSON の構文が不正です。" };
	}
	if (!isPlainObject(root)) return { error: "JSON はオブジェクトである必要があります。" };

	const unknownKey = Object.keys(root).find((key) => key !== "permissions");
	if (unknownKey !== undefined) return { error: `不明なキーがあります: ${unknownKey}` };
	if (!("permissions" in root)) return { tools: [], files: [] };

	const permissions = root.permissions;
	if (!isPlainObject(permissions))
		return { error: "permissions はオブジェクトである必要があります。" };
	const unknownAction = Object.keys(permissions).find(
		(key) => !ACTIONS.includes(key as PolicyAction),
	);
	if (unknownAction !== undefined)
		return { error: `permissions に不明なキーがあります: ${unknownAction}` };

	const tools: ToolPolicyRule[] = [];
	const files: FilePolicyRule[] = [];
	const toolKeys = new Set<string>();
	const filePatterns = new Set<string>();

	for (const action of ACTIONS) {
		const bucket = permissions[action];
		if (bucket === undefined) continue;
		if (!Array.isArray(bucket)) return { error: `${action} は配列である必要があります。` };
		for (const [index, raw] of bucket.entries()) {
			const position = `${action}の${index + 1}番目のエントリ`;
			if (typeof raw !== "string" || raw.trim() === "")
				return { error: `${position}: 空でない文字列を指定してください。` };
			const entry = raw.trim();
			const parsed = parseEntry(action, entry);
			if (typeof parsed === "string") return { error: `${position}: ${parsed}` };
			if (parsed.kind === "tool") {
				const key = JSON.stringify([action, parsed.rule.toolName, parsed.rule.argPattern]);
				if (toolKeys.has(key))
					return { error: `${position}: 同じルールが重複しています: ${entry}` };
				toolKeys.add(key);
				tools.push(parsed.rule);
			} else {
				if (filePatterns.has(parsed.rule.pattern))
					return { error: `${position}: 同じルールが重複しています: ${entry}` };
				filePatterns.add(parsed.rule.pattern);
				files.push(parsed.rule);
			}
		}
	}
	if (tools.length > MAX_RULES) return { error: `ツール実行ルールは${MAX_RULES}件までです。` };
	if (files.length > MAX_RULES) return { error: `対象ファイルのルールは${MAX_RULES}件までです。` };
	return { tools, files };
}

type ParsedEntry = { kind: "tool"; rule: ToolPolicyRule } | { kind: "file"; rule: FilePolicyRule };

/** ツールルール 1 件を、ポリシーの JSON と同じ書き方 (例: Bash(git push*)) にする。 */
export function formatToolPolicyEntry(
	rule: Pick<ToolPolicyRule, "toolName" | "argPattern">,
): string {
	const toolName = rule.toolName === null ? "*" : policyToolName(rule.toolName);
	return rule.argPattern === null ? toolName : `${toolName}(${rule.argPattern})`;
}

/** ファイルルール 1 件を、ポリシーの JSON と同じ書き方 (例: Edit(plans/**)) にする。 */
export function formatFilePolicyEntry(rule: FilePolicyRule): string {
	return `${FILE_ENTRY_BY_ACCESS[rule.access].verb}(${rule.pattern})`;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** エントリ 1 件を解釈する。エラーは文字列で返す。 */
function parseEntry(action: PolicyAction, entry: string): ParsedEntry | string {
	if (entry === "*") return { kind: "tool", rule: { action, toolName: null, argPattern: null } };

	const open = entry.indexOf("(");
	if (open < 0) return parseBareToolEntry(action, entry);
	// 括弧の中身は最初の ( から末尾の ) までとし、内側の括弧は中身として扱う。
	if (open === 0 || !entry.endsWith(")")) return `エントリの形式が不正です: ${entry}`;
	const name = entry.slice(0, open);
	const content = entry.slice(open + 1, -1).trim();
	if (name === "*") return "* に引数パターンは指定できません。";
	if (name === "Read" || name === "Edit") return parseFileEntry(action, name, content);
	return parseToolEntry(action, name, content);
}

function parseBareToolEntry(action: PolicyAction, name: string): ParsedEntry | string {
	if (name === "Read" || name === "Edit")
		return `${name} にはグロブの指定が必要です: ${name}(グロブ)`;
	if (!POLICY_TOOL_NAMES.has(name)) return `登録されていないツールです: ${name}`;
	return { kind: "tool", rule: { action, toolName: runtimeToolName(name), argPattern: null } };
}

function parseFileEntry(
	action: PolicyAction,
	verb: "Read" | "Edit",
	pattern: string,
): ParsedEntry | string {
	if (verb === "Read" && action !== "deny") return "Read(...) は deny でのみ指定できます。";
	const error = filePolicyRulePatternError(pattern);
	if (error !== null) return error;
	const access = verb === "Read" ? "denied" : ACCESS_BY_EDIT_ACTION[action];
	return { kind: "file", rule: { access, pattern } };
}

function parseToolEntry(
	action: PolicyAction,
	name: string,
	argPattern: string,
): ParsedEntry | string {
	if (argPattern === "") return "引数パターンを入力してください。";
	if (!POLICY_TOOL_NAMES.has(name)) return `登録されていないツールです: ${name}`;
	if (!ARG_PATTERN_TOOL_NAMES.has(name))
		return "argPattern は Bash または start_monitor にだけ指定できます。";
	const error = toolPolicyArgPatternError(argPattern);
	if (error !== null) return error;
	return { kind: "tool", rule: { action, toolName: runtimeToolName(name), argPattern } };
}

/** サーバ側 (UserToolRuleStore.Validate / UserToolRuleMatcher.IsValid) と同じ入力エラーを返す。 */
function toolPolicyArgPatternError(argPattern: string): string | null {
	if (argPattern.length > MAX_ARG_PATTERN_LENGTH)
		return `argPattern は${MAX_ARG_PATTERN_LENGTH}文字以内にしてください。`;
	// "*" 単独は照合トークンが空になり何にも一致しない (サーバ側 IsValid と同じ理由で拒否)。
	if (argPattern === "*")
		return "argPattern に * 単独は指定できません。ツール全体を対象にする場合は引数パターンを省略してください。";
	const wildcard = argPattern.indexOf("*");
	if (hasControlCharacter(argPattern) || (wildcard >= 0 && wildcard !== argPattern.length - 1))
		return "argPattern は制御文字を含めず、末尾の * を1個だけ使用できます。";
	return null;
}
